import { ReactNode } from 'react';

import type JasperDefault from '@bellmedia/jasper-player-tv';

import { IntentAction } from '@/enums/intentAction';

import { system as Hisense } from './hisense';
import { system as LG } from './lg';
import { system as PS5 } from './ps5';
import { system as Samsung } from './samsung';
import { system as XBOX } from './xbox';
import { system as Xfinity } from './xfinity';

export type SubscriptionCallback = (status: string) => void;

export type Subscription = {
  subscribe: (callback: SubscriptionCallback) => () => void;
  notify: (status: NetworkStatus) => void;
  clear: () => void;
};

export enum NetworkStatus {
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
}

export type MediaEvent =
  | 'startContent'
  | 'stopContent'
  | 'play'
  | 'loadStart'
  | 'playing'
  | 'paused'
  | 'buffering'
  | 'ended'
  | 'seeking'
  | 'seeked'
  | 'renditionChange';

export type MediaEventPayload = {
  entityId: string;
  position?: number;
  agePolicy?: string;
  bitrate?: number;
  width?: number;
  height?: number;
  videoCodec?: string;
};

export type System = {
  id: string;
  init: () => void;
  /** Fetches and configures the player ahead of playback. See usePlayerPreload. */
  preloadPlayer?: () => Promise<void>;
  /** Platform setup Jasper needs before a player is created. Idempotent. */
  configurePlayer?: (jasper: typeof JasperDefault) => void;
  version: () => string;
  script: () => ReactNode;
  keyThrottleDelay: number;
  key: (event: KeyboardEvent) => string;
  getDeviceId: () => Promise<string>;
  getErrorCodePrefix: () => string;
  getDeviceRootRem?: () => ROOT_REMS;
  closeApp: () => void;
  getDeviceAdId: () => Promise<string>;
  getAdIdType: () => Promise<string>;
  getIsLimitedAdTracking: () => Promise<boolean>;
  monitorConnectionStatus: (callback: NetworkCallback) => (() => void) | void;
  hasPointer: boolean;
  overrideAria?: boolean;
  logout?: () => void;
  disableExitPrompt?: boolean;
  exitDirectly?: boolean;
  initDeepLink?: (
    onDeepLink: (
      deepLinkId: string,
      action?: IntentAction,
      data?: { query?: string; sectionName?: string }
    ) => void
  ) => (() => void) | void;
  getDeepLinkId?: () => string | null;
  getDeviceLanguage?: () => Promise<string>;
  listenForDeviceLanguageChanges?: (
    onChange: (language: string) => void
  ) => (() => void) | void;
  logError?: (
    errorType: string,
    errorCode: string,
    errorMessage: string,
    isFatal: boolean,
    agePolicy?: string
  ) => void;
  logMediaEvent?: (eventName: MediaEvent, payload: MediaEventPayload) => void;
  logMediaError?: (
    errorCode: string,
    errorMessage: string,
    isFatal: boolean,
    agePolicy?: string
  ) => void;
};

export const getSystem = (): System => {
  switch (process.env.NEXT_PUBLIC_TV) {
    case 'lg':
      return LG;
    case 'hisense':
      return Hisense;
    case 'xbox':
      return XBOX;
    case 'ps5':
      return PS5;
    case 'xfinity':
      return Xfinity;
    case 'samsung':
    default:
      return Samsung;
  }
};

/**
 * Is this the Xfinity system? Used to determine if we should apply Xfinity-specific behavior in shared components.
 * @param system The system to check. Defaults to the current system.
 * @returns True if the system is Xfinity, false otherwise.
 */
export const isXfinitySystem = (system: Pick<System, 'id'>): boolean =>
  system.id === 'xfinity';

/**
 * Platforms that struggle with large screen payloads: they disable infinite-scroll
 * containers and fetch each non-SUBNAV container's items on demand, so they request
 * the trimmed screen document instead of the full one.
 * @param platform A system id, e.g. `getSystem().id`.
 */
export const isLowerPerformingPlatform = (platform: string): boolean =>
  ['ps5', 'xfinity'].includes(platform.trim().toLowerCase());

export const isLGMagicRemote = () => {
  if (
    process.env.NEXT_PUBLIC_TV === 'lg' &&
    typeof window !== 'undefined' &&
    window.matchMedia
  ) {
    return window.matchMedia('(any-pointer: fine)').matches;
  }

  return false;
};

export const Keys = {
  UP: 'UP',
  DOWN: 'DOWN',
  LEFT: 'LEFT',
  RIGHT: 'RIGHT',

  STOP: 'STOP',
  PLAY: 'PLAY',
  PAUSE: 'PAUSE',
  PLAY_PAUSE: 'PLAY_PAUSE',
  FAST_FORWARD: 'FAST_FORWARD',
  REWIND: 'REWIND',

  OK: 'OK',
  BACK: 'BACK',
  EXIT: 'EXIT',
  UNKNOWN: 'UNKNOWN',
};

export type ROOT_REMS = '9.67px' | '24px' | '16px' | '10.666px';

export type NetworkCallback = (isConnected: boolean) => void;
